import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import {
  Columns,
  Figure,
  Ledger,
  RankedBars,
  ReferenceDots,
  StackedBars,
  STACK_GRAYS,
  seriesColors,
  type StackKey,
} from "~/components/charts";
import { api, type ApiResponse } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import { AIReviewSection } from "./components/AIReviewSection";
import { Masthead } from "./components/Masthead";
import { buildPersonDek, buildPersonHeadline, firstName } from "./story";
import type { PeriodMeta, PriorSnapshot, TeamMedians, WorkCategoryShare } from "./types";
import { changeText, fmtDay, fmtHours, fmtNum, periodName, priorName } from "~/lib/format";
import { ed } from "~/components/editorial";

interface ShippedPr {
  id: string;
  number: number;
  title: string;
  repo: string;
  repoFullName: string;
  workCategory: string;
  jiraKey: string | null;
  additions: number;
  deletions: number;
  aiAssisted: boolean;
  mergedAt: string | null;
  hoursToMerge: number | null;
}

interface TeamPerson {
  id: string;
  prsMerged: number;
  reviewsGiven: number;
  decisiveReviews: number;
  totalCommits: number;
  effectiveIssuesCompleted: number;
  medianHoursToMerge: number | null;
}

interface DeveloperStats {
  developer: {
    id: string;
    displayName: string;
    githubUsername: string | null;
    avatarUrl: string | null;
    email: string | null;
    teams: Array<{ id: string; name: string }>;
    lastActivityAt: string | null;
  };
  stats: {
    prsCreated: number;
    prsMerged: number;
    mergeRate: number;
    reviewsGiven: number;
    totalCommits: number;
    avgPrSize: number;
    avgTimeToMerge: number;
    avgTimeToFirstReview: number;
    storyPoints: number;
    issuesCompleted: number;
  };
  charts: {
    activityTimeline: Array<{ date: string; prs: number; reviews: number; total: number }>;
    reviewTypes: Array<{ name: string; value: number }>;
  };
  topCollaborators: Array<{
    id: string;
    displayName: string;
    githubUsername: string | null;
    count: number;
  }>;
  workMix: {
    categories: WorkCategoryShare[];
    total: number;
    linkedToJira: number;
    linkedPercentage: number;
  };
  // Present once the server is on periods
  period?: PeriodMeta;
  current?: PriorSnapshot | null;
  previous?: PriorSnapshot | null;
  team?: {
    medians: TeamMedians;
    previousMedians: TeamMedians;
    people: TeamPerson[];
  };
  shipped?: ShippedPr[];
}

const CATEGORY_LABELS: Record<string, string> = {
  feature: "Feature",
  bug: "Bug fix",
  tech_debt: "Tech debt",
  docs: "Docs",
  release: "Release",
  infra: "Infra",
  other: "Other",
};

/** 1-based rank of `value` among `values`, highest first, ties sharing a rank. */
function rankOf(value: number, values: number[]): number {
  return values.filter((v) => v > value).length + 1;
}

export function DeveloperDetailPage() {
  const { developerId } = useParams<{ developerId: string }>();
  const [searchParams] = useSearchParams();
  const { range, window } = usePeriod();
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";

  const { data, isLoading, error } = useQuery({
    queryKey: ["developer-stats", developerId, range],
    queryFn: () =>
      api.get<ApiResponse<DeveloperStats>>(`/developers/${developerId}/stats?range=${range}`),
    enabled: !!developerId,
  });

  const d = data?.data;
  const period = d?.period ?? {
    label: window.label,
    prevLabel: window.prevLabel,
    asOf: new Date().toISOString(),
  };
  const now = periodName(period.label);
  const prior = priorName(period.prevLabel);

  const model = useMemo(() => {
    if (!d) return null;
    const cur = d.current;
    const prev = d.previous ?? null;
    const med = d.team?.medians;
    const merged = cur?.prsMerged ?? d.stats.prsMerged;
    const reviews = cur?.reviewsGiven ?? d.stats.reviewsGiven;
    const people = d.team?.people ?? [];
    return {
      cur,
      prev,
      med,
      merged,
      reviews,
      peopleCount: people.length,
      mergedRank: people.length ? rankOf(merged, people.map((p) => p.prsMerged)) : null,
      reviewRank: people.length ? rankOf(reviews, people.map((p) => p.reviewsGiven)) : null,
      story: {
        displayName: d.developer.displayName,
        periodLabel: period.label,
        prevLabel: period.prevLabel,
        prsMerged: merged,
        prevPrsMerged: prev ? prev.prsMerged : null,
        reviewsGiven: reviews,
        prevReviewsGiven: prev ? prev.reviewsGiven : null,
        teamMedianPrsMerged: med?.prsMerged ?? null,
        teamMedianReviews: med?.reviewsGiven ?? null,
      },
    };
  }, [d, period.label, period.prevLabel]);

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead periodLabel={period.label} prevLabel={period.prevLabel} asOf={period.asOf} />
        <Link to={`/developers${suffix}`} className={styles.back}>
          ← All developers
        </Link>

        {isLoading ? (
          <div className={ed.loading}>Loading…</div>
        ) : error || !d || !model ? (
          <div className={ed.loading}>Developer not found.</div>
        ) : (
          <>
            <div className={ed.kicker}>
              {d.developer.teams.map((t) => t.name).join(", ") || "Developer"} · {now}
            </div>
            <h1 className={ed.headline}>{buildPersonHeadline(model.story)}</h1>
            <p className={ed.dek}>{buildPersonDek(model.story)}</p>
            <div className={ed.byline}>
              <b>{d.developer.displayName}</b>
              {d.developer.githubUsername && <> · @{d.developer.githubUsername}</>}
              {model.mergedRank !== null && (
                <>
                  {" "}· <span className="num">{ordinal(model.mergedRank)}</span> of{" "}
                  <span className="num">{model.peopleCount}</span> in merged PRs,{" "}
                  <span className="num">{ordinal(model.reviewRank ?? 0)}</span> in PRs reviewed
                </>
              )}
              {d.developer.lastActivityAt && (
                <>
                  {" "}· last active{" "}
                  <span className="num">
                    {new Date(d.developer.lastActivityAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </>
              )}
            </div>

            <Ledger
              items={[
                {
                  label: "PRs merged",
                  current: model.merged,
                  prior: model.prev?.prsMerged ?? null,
                  ...changeText(model.merged, model.prev?.prsMerged ?? null),
                },
                {
                  label: "PRs reviewed",
                  current: model.reviews,
                  prior: model.prev?.reviewsGiven ?? null,
                  note: "by other people",
                  ...changeText(model.reviews, model.prev?.reviewsGiven ?? null),
                },
                {
                  label: "Reviews decided",
                  current: model.cur?.decisiveReviews ?? null,
                  prior: model.prev?.decisiveReviews ?? null,
                  note: "approved or changes asked",
                  ...changeText(model.cur?.decisiveReviews ?? null, model.prev?.decisiveReviews ?? null),
                },
                {
                  label: "Tickets",
                  current: model.cur?.effectiveIssuesCompleted ?? d.stats.issuesCompleted,
                  prior: model.prev?.effectiveIssuesCompleted ?? null,
                  ...changeText(
                    model.cur?.effectiveIssuesCompleted ?? d.stats.issuesCompleted,
                    model.prev?.effectiveIssuesCompleted ?? null,
                  ),
                },
                {
                  label: "Commits",
                  current: model.cur?.totalCommits ?? d.stats.totalCommits,
                  prior: model.prev?.totalCommits ?? null,
                  ...changeText(
                    model.cur?.totalCommits ?? d.stats.totalCommits,
                    model.prev?.totalCommits ?? null,
                  ),
                },
                {
                  label: "Open to merge",
                  current: model.cur?.medianHoursToMerge ?? null,
                  prior: model.prev?.medianHoursToMerge ?? null,
                  format: fmtHours,
                  note: "median",
                  ...changeText(
                    model.cur?.medianHoursToMerge ?? null,
                    model.prev?.medianHoursToMerge ?? null,
                    { minBaseForPct: 0 },
                  ),
                },
              ]}
            />

            <div className={ed.sect}>I. Against the team</div>
            <h2 className={ed.h2}>
              {firstName(d.developer.displayName)} against the team median, {now}
            </h2>
            {model.med ? (
              <Figure
                title={`${d.developer.displayName}, the team median and ${prior}`}
                sub={`Filled dot is ${firstName(d.developer.displayName)} this period. Hollow dots are the team median and their own ${prior}.`}
                method={`Team median over the ${fmtNum(model.med.people)} people with code work in ${now}. Each row on its own scale.`}
                table={{
                  columns: ["Measure", d.developer.displayName, "Team median", prior],
                  rows: refRows(model).map((r) => [
                    r.label,
                    r.value === null ? "–" : (r.format ?? fmtNum)(r.value),
                    r.references[0]?.value == null ? "–" : (r.format ?? fmtNum)(r.references[0].value),
                    r.references[1]?.value == null ? "–" : (r.format ?? fmtNum)(r.references[1].value),
                  ]),
                }}
              >
                <ReferenceDots
                  ariaLabel="Person against the team median"
                  subjectLabel={d.developer.displayName}
                  rows={refRows(model).map((r) => ({
                    ...r,
                    references: [
                      { label: "Team median", value: r.references[0]?.value ?? null },
                      { label: prior, value: r.references[1]?.value ?? null },
                    ],
                  }))}
                />
              </Figure>
            ) : (
              <p className={ed.prose}>Team comparison isn't available for this period.</p>
            )}

            <div className={ed.sect}>II. What shipped</div>
            <h2 className={ed.h2}>
              {plainCount(d.shipped?.length ?? d.stats.prsMerged, "PR")} merged in {now}
            </h2>
            <div className={ed.twoUp}>
              <ShippedTable shipped={d.shipped ?? []} />
              <WorkMix current={model.cur?.workCategories ?? d.workMix.categories} previous={model.prev?.workCategories ?? []} now={now} prior={prior} linked={d.workMix.linkedPercentage} />
            </div>

            <div className={ed.sect}>III. Day by day</div>
            <div className={ed.twoUp}>
              <Figure
                title={`PRs opened and reviews given each day, ${now}`}
                method="PRs by the date they were opened; reviews by submission date, counting each review submitted."
              >
                <Columns
                  ariaLabel="Daily PRs opened and reviews"
                  valueLabel="PRs opened"
                  stackLabel="Reviews"
                  legend="inline"
                  formatKey={fmtDay}
                  tickFormat={(t) => String(Math.round(t))}
                  margin={{ t: 22, l: 28 }}
                  steps={2}
                  maxLabels={8}
                  points={d.charts.activityTimeline.map((p) => ({
                    key: p.date,
                    value: p.prs,
                    stack: p.reviews,
                  }))}
                />
              </Figure>
              <div>
                {d.topCollaborators.length > 0 && (
                  <Figure
                    title={`Who reviewed ${firstName(d.developer.displayName)}'s PRs`}
                    method="Review submissions on their PRs opened in the period, by reviewer."
                  >
                    <RankedBars
                      width={420}
                      ariaLabel="Reviewers of this person's PRs"
                      rows={d.topCollaborators.map((c) => ({
                        id: c.id,
                        label: c.displayName,
                        value: c.count,
                      }))}
                    />
                  </Figure>
                )}
                {d.charts.reviewTypes.length > 0 && (
                  <Figure
                    title="How their reviews landed"
                    method="Review submissions on other people's PRs, by outcome."
                  >
                    <RankedBars
                      width={420}
                      ariaLabel="Review outcomes"
                      rows={d.charts.reviewTypes.map((r) => ({
                        id: r.name,
                        label: r.name,
                        value: r.value,
                      }))}
                    />
                  </Figure>
                )}
              </div>
            </div>

            <div className={ed.sect}>IV. Written review</div>
            <div className={styles.aiWrap}>
              <AIReviewSection
                developerId={developerId!}
                developerName={d.developer.displayName}
                range={range}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type Model = {
  cur: PriorSnapshot | null | undefined;
  prev: PriorSnapshot | null;
  med: TeamMedians | undefined;
  merged: number;
  reviews: number;
};

function refRows(m: Model) {
  return [
    {
      label: "PRs merged",
      value: m.merged,
      references: [{ value: m.med?.prsMerged ?? null }, { value: m.prev?.prsMerged ?? null }],
      format: undefined as ((v: number) => string) | undefined,
      lowerIsBetter: false,
    },
    {
      label: "PRs reviewed",
      value: m.reviews,
      references: [{ value: m.med?.reviewsGiven ?? null }, { value: m.prev?.reviewsGiven ?? null }],
      format: undefined,
      lowerIsBetter: false,
    },
    {
      label: "Reviews decided",
      value: m.cur?.decisiveReviews ?? null,
      references: [{ value: m.med?.decisiveReviews ?? null }, { value: m.prev?.decisiveReviews ?? null }],
      format: undefined,
      lowerIsBetter: false,
    },
    {
      label: "Tickets",
      value: m.cur?.effectiveIssuesCompleted ?? null,
      references: [
        { value: m.med?.effectiveIssuesCompleted ?? null },
        { value: m.prev?.effectiveIssuesCompleted ?? null },
      ],
      format: undefined,
      lowerIsBetter: false,
    },
    {
      label: "Open to merge",
      value: m.cur?.medianHoursToMerge ?? null,
      references: [
        { value: m.med?.medianHoursToMerge ?? null },
        { value: m.prev?.medianHoursToMerge ?? null },
      ],
      format: fmtHours as (v: number) => string,
      lowerIsBetter: true,
    },
  ];
}

function ShippedTable({ shipped }: { shipped: ShippedPr[] }) {
  if (shipped.length === 0) {
    return <p className={ed.prose}>Nothing merged in this period yet.</p>;
  }
  return (
    <Figure
      title="Merged PRs, newest first"
      method="PRs they authored that merged in the window. Size is lines added plus removed. AI marks PRs flagged as agent-written or agent-assisted."
    >
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Merged</th>
              <th className={styles.left}>PR</th>
              <th className={styles.left}>Kind</th>
              <th>Size</th>
              <th>To merge</th>
              <th>AI</th>
            </tr>
          </thead>
          <tbody>
            {shipped.map((pr) => (
              <tr key={pr.id}>
                <td>
                  {pr.mergedAt
                    ? new Date(pr.mergedAt).toLocaleDateString("en-US", { month: "short", day: "2-digit" })
                    : "–"}
                </td>
                <td className={styles.title}>
                  <span className={styles.ref}>
                    {pr.repo}#{pr.number}
                    {pr.jiraKey ? ` · ${pr.jiraKey}` : ""}
                  </span>
                  {pr.title}
                </td>
                <td className={styles.kind}>{CATEGORY_LABELS[pr.workCategory] ?? pr.workCategory}</td>
                <td>{fmtNum(pr.additions + pr.deletions)}</td>
                <td>{fmtHours(pr.hoursToMerge)}</td>
                <td>{pr.aiAssisted ? "●" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Figure>
  );
}

function WorkMix({
  current,
  previous,
  now,
  prior,
  linked,
}: {
  current: WorkCategoryShare[];
  previous: WorkCategoryShare[];
  now: string;
  prior: string;
  linked: number;
}) {
  const order = [...current].sort((a, b) => b.count - a.count).map((c) => c.category);
  for (const c of previous) if (!order.includes(c.category)) order.push(c.category);
  const labelOf = (k: string) =>
    current.find((c) => c.category === k)?.label ?? previous.find((c) => c.category === k)?.label ?? k;
  const keys: StackKey[] = order.map((k, i) => ({
    key: k,
    label: labelOf(k),
    color: i < 2 ? seriesColors[i]! : STACK_GRAYS[Math.min(i - 2, STACK_GRAYS.length - 1)]!,
  }));
  const rows = [
    { id: "now", label: now, values: Object.fromEntries(current.map((c) => [c.category, c.count])) },
    ...(previous.length
      ? [{ id: "prev", label: prior, values: Object.fromEntries(previous.map((c) => [c.category, c.count])) }]
      : []),
  ];
  if (keys.length === 0) return null;
  return (
    <Figure
      title="What kind of work it was"
      sub={`Merged PRs by category, ${now} against ${prior}`}
      method={`Category from the linked JIRA issue type, else branch and title. ${linked}% of this period's merged PRs link to a JIRA ticket.`}
    >
      <StackedBars ariaLabel="Merged PRs by category" keys={keys} rows={rows} mode="count" />
    </Figure>
  );
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

function plainCount(n: number, noun: string): string {
  return `${fmtNum(n)} ${noun}${n === 1 ? "" : "s"}`;
}

const styles = {
  back: css({
    display: "inline-block",
    mt: "12px",
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2!",
    textDecoration: "none",
    _hover: { textDecoration: "underline" },
  }),
  scroll: css({ overflowX: "auto", borderTopWidth: "2px", borderTopStyle: "solid", borderColor: "ed.ink" }),
  table: css({
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
    "& th": {
      fontFamily: "sans",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: "ed.ink3",
      textAlign: "right",
      py: "5px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
    },
    "& td": {
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
      textAlign: "right",
      py: "4px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.grid",
      verticalAlign: "top",
      whiteSpace: "nowrap",
    },
  }),
  left: css({ textAlign: "left!" }),
  title: css({
    textAlign: "left!",
    fontFamily: "sans!",
    whiteSpace: "normal!",
    color: "ed.ink",
    minWidth: "220px",
  }),
  ref: css({
    display: "block",
    fontFamily: "mono",
    fontSize: "10.5px",
    color: "ed.ink3",
  }),
  kind: css({ textAlign: "left!", fontFamily: "sans!", color: "ed.ink2" }),
  aiWrap: css({ mt: "12px" }),
};
